import { Formik, useFormikContext } from 'formik';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { HiOutlineCheck, HiOutlineExclamation, HiOutlineX, HiOutlineInformationCircle } from 'react-icons/hi';
import { seoSchema } from '../../../constants';
import Input from '../InputField';
import NewKeyWord from '../Keywords';
import { DAV_APIS } from '../../../adapters';

// --- Types ---
interface initialDataProps {
  slug?: string;
  title?: string;
  description?: string;
  image?: string;
  key_words?: string[];
  content?: string;
  // Advanced fields
  canonicalUrl?: string;
  robotsIndex?: string;
  robotsFollow?: string;
  schemaPageType?: string;
  schemaArticleType?: string;
  focusKeyword?: string;
}

interface SEOProps {
  isEdit?: boolean;
  initialData?: initialDataProps;
  pageType: string;
  isModal?: boolean;
  setRefresh?: any;
  setShowModal?: any;
}

// --- Helpers & Constants ---

const SCHEMA_PAGE_TYPES = [
    { label: 'Web Page', value: 'WebPage' },
    { label: 'Item Page', value: 'ItemPage' },
    { label: 'About Page', value: 'AboutPage' },
    { label: 'FAQ Page', value: 'FAQPage' },
    { label: 'Contact Page', value: 'ContactPage' },
    { label: 'Real Estate Listing', value: 'RealEstateListing' },
    { label: 'Profile Page', value: 'ProfilePage' },
];

const SCHEMA_ARTICLE_TYPES = [
    { label: 'None', value: 'None' },
    { label: 'Article', value: 'Article' },
    { label: 'Blog Posting', value: 'BlogPosting' },
    { label: 'News Article', value: 'NewsArticle' },
    { label: 'Scholarly Article', value: 'ScholarlyArticle' },
    { label: 'Tech Article', value: 'TechArticle' },
];

const AnalysisItem = ({ level, msg }: { level: string, msg: string }) => {
  let icon = <HiOutlineCheck className='w-4 h-4 text-success' />;
  let colorClass = 'text-gray-700 dark:text-gray-300'; // Standard text color for better readability

  if (level === 'red') {
    icon = <HiOutlineX className='w-4 h-4 text-error' />;
    colorClass = 'text-error';
  } else if (level === 'orange') {
     icon = <HiOutlineExclamation className='w-4 h-4 text-warning' />;
     colorClass = 'text-warning';
  } else if (level === 'gray') {
    icon = <HiOutlineInformationCircle className='w-4 h-4 text-gray-400' />;
    colorClass = 'text-gray-500';
  }

  return (
    <div className='flex items-start gap-2 text-sm max-w-full break-words'>
      <span className='mt-0.5 flex-shrink-0'>{icon}</span>
      <span className={`${colorClass}`}>{msg}</span>
    </div>
  );
};

const ProgressBar = ({ value, max, goodRange }: { value: number, max: number, goodRange: [number, number] }) => {
  const percentage = Math.min((value / max) * 100, 100);
  let color = 'bg-warning';
  if (value >= goodRange[0] && value <= goodRange[1]) color = 'bg-success';
  if (value > goodRange[1]) color = 'bg-error';

  return (
    <div className='w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden'>
      <div className={`h-full ${color} transition-all duration-300`} style={{ width: `${percentage}%` }} />
    </div>
  );
};

const GooglePreview = ({ values, slug, isMobile }: { values: any, slug: string, isMobile: boolean }) => {
  const title = values.title || 'Please provide a meta title';
  const desc = values.description || 'Please provide a meta description by editing the snippet below.';
  const pageSlug = slug || 'page-slug';
  const displayUrl = `davsafaris.com › ${pageSlug}`;

  return (
    <div className={`font-arial bg-white dark:bg-[#202124] p-4 rounded-lg border border-gray-100 dark:border-gray-700 ${isMobile ? 'max-w-[400px]' : 'w-full'} transition-colors duration-200`}>
      <div className='flex items-center gap-2 mb-1'>
         <div className='bg-gray-100 dark:bg-gray-800 rounded-full p-1'>
             <img src='/dav-safaris-logo.png' alt='Logo' className='w-4 h-4 object-contain opacity-70' />
         </div>
         <div className='flex flex-col text-sm leading-tight'>
            <span className='text-[#202124] dark:text-[#e8eaed]'>Dav Safaris</span>
            <span className='text-[12px] text-[#5f6368] dark:text-[#bdc1c6]'>{displayUrl}</span>
         </div>
      </div>
      <h3 className='text-[20px] leading-[1.3] text-[#1a0dab] dark:text-[#8ab4f8] hover:underline cursor-pointer font-normal truncate mb-1'>
        {title}
      </h3>
      <div className='text-[14px] leading-[1.58] text-[#4d5156] dark:text-[#bdc1c6] line-clamp-2'>
        {desc}
      </div>
    </div>
  );
};

// --- Analysis Logic ---

const analyzeSEO = (values: any, focusKeyword: string) => {
  const issues = [];
  const good = [];
  
  const title = values.title || '';
  const desc = values.description || '';
  const slug = values.slug || '';
  const keyword = focusKeyword?.toLowerCase() || '';

  // 1. Keyphrase Length
  if (keyword.length === 0) {
    issues.push({ level: 'gray', msg: 'No focus keyphrase set.' });
  }

  if (keyword) {
    // 2. Keyphrase in Title
    if (title.toLowerCase().includes(keyword)) {
      good.push({ msg: 'Keyphrase appears in the SEO title.' });
    } else {
      issues.push({ level: 'red', msg: 'Keyphrase does not appear in the SEO title.' });
    }

    // 3. Keyphrase in Intro
    if (desc.toLowerCase().startsWith(keyword)) {
      good.push({ msg: 'Keyphrase appears at the start of the meta description.' });
    } else if (desc.toLowerCase().includes(keyword)) {
      good.push({ msg: 'Keyphrase appears in the meta description.' });
    } else {
      issues.push({ level: 'orange', msg: 'Keyphrase does not appear in the meta description.' });
    }

    // 4. Keyphrase in Slug
    if (slug.toLowerCase().includes(keyword.replace(/\s+/g, '-'))) {
       good.push({ msg: 'Keyphrase appears in the slug.' });
    } else {
       // Optional warning
    }
  }

  // 5. Title Length
  if (title.length > 0) {
    if (title.length < 30) issues.push({ level: 'orange', msg: 'SEO title is too short.' });
    else if (title.length > 60) issues.push({ level: 'red', msg: 'SEO title is too long.' });
    else good.push({ msg: 'SEO title length is good.' });
  } else {
     issues.push({ level: 'red', msg: 'Please provide an SEO title.' });
  }

  // 6. Desc Length
  if (desc.length > 0) {
    if (desc.length < 50) issues.push({ level: 'orange', msg: 'Meta description is too short.' });
    else if (desc.length > 160) issues.push({ level: 'orange', msg: 'Meta description is too long.' });
    else good.push({ msg: 'Meta description length is good.' });
  } else {
    issues.push({ level: 'red', msg: 'Please provide a meta description.' });
  }

  let score = 'green';
  if (issues.some((i) => i.level === 'red')) {
    score = 'red';
  } else if (issues.some((i) => i.level === 'orange')) {
    score = 'orange';
  }

  return { issues, good, score };
};

const analyzeReadability = (content: string) => {
  const issues: { level: string, msg: string }[] = [];
  const good: { msg: string }[] = [];
  
  if (!content) {
      issues.push({ level: 'gray', msg: 'Not enough content to analyze readability.' });
      return { issues, good, score: 'gray' };
  }

  const wordCount = content.split(/\s+/).length;
  if (wordCount < 300) {
      issues.push({ level: 'orange', msg: `Text length: The text contains ${wordCount} words. This is slightly below the recommended minimum of 300 words.` });
  } else {
      good.push({ msg: 'Text length: Good job!' });
  }

  // Simplified Paragraph Length
  const paragraphs = content.split(/\n\s*\n/);
  const longParagraphs = paragraphs.filter(p => p.split(/\s+/).length > 150);
  if (longParagraphs.length > 0) {
      issues.push({ level: 'orange', msg: 'Paragraph length: Some paragraphs are too long.' });
  } else {
      good.push({ msg: 'Paragraph length: None of the paragraphs are too long. Great job!' });
  }

  // Simplified Sentence Length
  const sentences = content.split(/[.!?]+/);
  const longSentences = sentences.filter(s => s.split(/\s+/).length > 20);
  if (longSentences.length > (sentences.length * 0.25)) {
      issues.push({ level: 'red', msg: 'Sentence length: More than 25% of sentences contain more than 20 words.' });
  } else {
      good.push({ msg: 'Sentence length: Great!' });
  }

  let score = 'green';
  if (issues.some((i) => i.level === 'red')) score = 'red';
  else if (issues.some((i) => i.level === 'orange')) score = 'orange';

  return { issues, good, score };
};

// --- SEOSync Component ---

const SEOSync = ({ initialData, hasSavedData }: { initialData?: initialDataProps, hasSavedData: boolean }) => {
  const { setFieldValue, touched, values } = useFormikContext<any>();

  useEffect(() => {
    if (!hasSavedData) {
      if (!touched.title && initialData?.title && initialData.title !== values.title) {
        setFieldValue('title', initialData.title);
      }
      
      // Sync Description: Strip HTML tags as it's likely a rich text field from parent
      if (!touched.description && initialData?.description) {
        const plainDesc = initialData.description.replace(/<[^>]+>/g, '');
        if (plainDesc !== values.description) {
           setFieldValue('description', plainDesc);
        }
      }
      
      // Sync Slug from Title if not manually touched
      if (!touched.slug) {
          const sourceTitle = values.title || initialData?.title || '';
          const generatedSlug = sourceTitle.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          if (generatedSlug !== values.slug && generatedSlug.length > 0) {
              setFieldValue('slug', generatedSlug);
          }
      }
    }
  }, [initialData?.title, initialData?.description, hasSavedData, touched, setFieldValue, values.title, values.description, values.slug]);

  return null;
};

// --- Main Component ---

const SEO = (props: SEOProps) => {
  const { isEdit, initialData, pageType, isModal, setRefresh, setShowModal } = props;
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(isModal);
  const [isNew, setIsNew] = useState(true);
  const [focusKeyword, setFocusKeyword] = useState('');
  const [activeTab, setActiveTab] = useState('seo');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const getSeoData = async () => {
    if (!initialData?.slug) return;
    try {
      setLoading(true);
       // Assuming DAV_APIS exists and works
      const res = await DAV_APIS.getSearchEngineData(initialData?.slug as string);
      if (!res.data.seo) {
        setIsNew(true);
        setLoading(false);
        return;
      }
      setData(res.data?.seo);
      setFocusKeyword(res.data?.seo?.focusKeyword || '');
      setIsNew(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Failed to fetch SEO data, Please try again');
    }
  };

  useEffect(() => {
    if (isEdit) {
      getSeoData();
    }
  }, [initialData?.slug, isEdit]);

  const seoSubmitHandler = async (values: any) => {
    try {
      setLoading(true);
      const newData = {
        title: values.title,
        description: values.description,
        SEOImage: values.image,
        keywords: values.key_words,
        slug: isEdit ? initialData?.slug : values.title.toLowerCase().replace(/\s/g, '-'),
        pageType,
        canonicalUrl: values.canonicalUrl,
        robotsIndex: values.robotsIndex,
        robotsFollow: values.robotsFollow,
        schemaPageType: values.schemaPageType,
        schemaArticleType: values.schemaArticleType,
        // focusKeyword could be passed if API supports it
      };
      if (!isNew && data?.id) {
        const updated = await DAV_APIS.updateSearchEngineData(newData, data?.id);
        setData(updated.data.seo);
        if (pageType === 'page') {
           await DAV_APIS.revalidateSearchEngineRoute(`/${initialData?.slug}`);
        }
        if (setRefresh) setRefresh(Math.random());
        if (isModal && setShowModal) setShowModal(false);
        setLoading(false);
        toast.success('SEO data updated successfully');
        return;
      }
      await DAV_APIS.createSearchEngineData(newData);
      setLoading(false);
      toast.success('SEO data submitted successfully');
    } catch (error) {
      setLoading(false);
      toast.error('Failed to update SEO, Please try again');
    }
  };

  const isDisabled = false;

  return (
    <Formik
      enableReinitialize={!!isEdit}
      initialValues={{
        title: data?.title || initialData?.title || '',
        description: data?.description || initialData?.description || '',
        image: data?.SEOImage || initialData?.image || '',
        key_words: data?.keywords || initialData?.key_words || [],
        slug: data?.slug || initialData?.slug || '',
        canonicalUrl: data?.canonicalUrl || initialData?.canonicalUrl || '',
        robotsIndex: data?.robotsIndex || initialData?.robotsIndex || 'index',
        robotsFollow: data?.robotsFollow || initialData?.robotsFollow || 'follow',
        schemaPageType: data?.schemaPageType || initialData?.schemaPageType || 'WebPage',
        schemaArticleType: data?.schemaArticleType || initialData?.schemaArticleType || 'None',
      }}
      validationSchema={seoSchema}
      onSubmit={seoSubmitHandler}
    >
      {({ handleSubmit, setFieldValue, values, dirty, isValid }) => {
        const seoAnalysis = analyzeSEO(values, focusKeyword);
        const readabilityAnalysis = analyzeReadability(initialData?.content || initialData?.description || '');
        
        // Overall score logic roughly combines both (simplified)
        let overallScore = 'green';
        if (seoAnalysis.score === 'red' || readabilityAnalysis.score === 'red') overallScore = 'red';
        else if (seoAnalysis.score === 'orange' || readabilityAnalysis.score === 'orange') overallScore = 'orange';

        const hasSavedData = !!data?.title;

        let scoreColor = 'bg-green-500';
        let scoreIcon = ':)';
        if (overallScore === 'orange') {
          scoreColor = 'bg-orange-500';
          scoreIcon = ':|';
        } else if (overallScore === 'red') {
          scoreColor = 'bg-red-500';
          scoreIcon = ':(';
        }

        const inputClasses = 'w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all text-sm text-gray-700 dark:text-gray-200';
        const labelClasses = 'block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5';

        return (
          <form onSubmit={handleSubmit}>
            <SEOSync initialData={initialData} hasSavedData={hasSavedData} />
            <div className='bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 rounded-xl my-6 overflow-hidden'>
               {/* Header / Accordion Toggle */}
              <div
                className='w-full flex justify-between gap-4 items-center p-5 cursor-pointer bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 select-none hover:bg-gray-100 dark:hover:bg-gray-800/80 transition-colors'
                onClick={() => setOpen(!open)}
              >
                <div className='flex items-center gap-3'>
                   <span className={`flex items-center justify-center w-8 h-8 rounded-full text-white font-bold text-sm shadow-sm ${scoreColor}`}>
                      {scoreIcon}
                   </span>
                   <div>
                     <h2 className='text-lg font-bold text-gray-900 dark:text-white'>SEO</h2>
                     {!isNew && (
                      <p className='text-xs text-gray-400'>
                        {isEdit ? 'Editing existing SEO data' : 'Creating new SEO data (Save separately)'}
                      </p>
                    )}
                   </div>
                </div>
                <button type='button' className='text-gray-500 hover:text-gray-700 dark:text-gray-400'>
                  {open ? <MdKeyboardArrowUp className='text-2xl' /> : <MdKeyboardArrowDown className='text-2xl' />}
                </button>
              </div>

              {/* Body */}
              <div className={`transition-all duration-300 ease-in-out ${open ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                
                <div className='p-6 space-y-8'>
                    {/* Tabs (Visual) */}
                    <div className='flex border-b border-gray-200 dark:border-gray-700 mb-8'>
                        {[
                            { id: 'seo', label: 'SEO' },
                            { id: 'readability', label: 'Readability' },
                            { id: 'schema', label: 'Schema' }
                        ].map(tab => (
                            <button 
                                key={tab.id}
                                type='button' 
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-6 py-3 font-medium text-sm transition-all border-b-2 relative top-[2px] ${activeTab === tab.id ? 'text-primary-600 border-primary-600' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-transparent hover:border-gray-300 dark:hover:border-gray-600'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* SEO Tab Content */}
                    {activeTab === 'seo' && (
                        <div className='space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300'>
                            {/* Focus Keyphrase */}
                            <div>
                                <label className={labelClasses}>
                                    Focus Keyphrase
                                </label>
                                <div className='relative'>
                                    <input 
                                        type='text' 
                                        className={inputClasses}
                                        placeholder='e.g. Luxury Safari'
                                        value={focusKeyword}
                                        onChange={(e) => setFocusKeyword(e.target.value)}
                                        disabled={isDisabled}
                                    />
                                    <HiOutlineInformationCircle className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
                                </div>
                                <p className='text-xs text-gray-500 mt-1.5'>The main keyword you want this page to rank for.</p>
                            </div>

                            {/* Google Preview */}
                            <div className='bg-gray-50 dark:bg-gray-900/50 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-inner'>
                                <h4 className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2'>
                                    <span className='w-1.5 h-4 bg-primary-500 rounded-full' />
                                    Google Preview
                                </h4>
                                <div className='space-y-4'>
                                     <GooglePreview values={values} slug={isEdit ? initialData?.slug as string : values.title.toLowerCase().replace(/\s/g, '-') } isMobile />
                                </div>
                            </div>

                            {/* SEO Inputs */}
                            <div className='space-y-6'>
                                 <div>
                                    <Input
                                        type='text'
                                        name='title'
                                        label='SEO Title'
                                        placeholder='Type meta title here'
                                        disabled={isDisabled}
                                    />
                                    <ProgressBar value={values.title.length} max={80} goodRange={[30, 60]} />
                                </div>

                                <div>
                                    <Input
                                        type='text'
                                        name='slug'
                                        label='Slug'
                                        placeholder='url-slug-example'
                                        disabled={isDisabled}
                                    />
                                    <p className='text-xs text-gray-500 mt-1.5'>The "slug" is the URL-friendly version of the name. It is usually all lowercase and contains only letters, numbers, and hyphens.</p>
                                </div>

                                <div>
                                    <Input
                                        type='textarea'
                                        rows={4}
                                        name='description'
                                        label='Meta Description'
                                        placeholder='Modify your meta description here'
                                        disabled={isDisabled}
                                    />
                                     <ProgressBar value={values.description.length} max={180} goodRange={[50, 160]} />
                                </div>

                                 <Input
                                    type='text'
                                    name='image'
                                    label='Social Image URL'
                                    placeholder='Paste the link of the image you want for sharing'
                                    disabled={isDisabled}
                                />

                                <NewKeyWord
                                    key_words={values.key_words}
                                    setKeys={(newWords: string[]) => setFieldValue('key_words', newWords)}
                                />
                            </div>

                            {/* Advanced SEO Toggle */}
                            <div className='border rounded-xl border-gray-200 dark:border-gray-700 overflow-hidden'>
                                <button
                                    type='button'
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    className='w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors'
                                >
                                    <span className='font-medium text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2'>
                                        <span className={`w-2 h-2 rounded-full ${showAdvanced ? 'bg-primary-500' : 'bg-gray-400'}`} />
                                        Advanced SEO
                                    </span>
                                    {showAdvanced ? <MdKeyboardArrowUp className='text-lg text-gray-500' /> : <MdKeyboardArrowDown className='text-lg text-gray-500' />}
                                </button>
                                {showAdvanced && (
                                    <div className='p-5 space-y-6 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 animate-in slide-in-from-top-2'>
                                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                            <div>
                                                <label className={labelClasses}>Allow search engines to show this Page?</label>
                                                <select
                                                    name='robotsIndex'
                                                    value={values.robotsIndex}
                                                    onChange={(e) => setFieldValue('robotsIndex', e.target.value)}
                                                    className={inputClasses}
                                                    disabled={isDisabled}
                                                >
                                                    <option value='index'>Yes (Index)</option>
                                                    <option value='noindex'>No (NoIndex)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className={labelClasses}>Follow links on this Page?</label>
                                                <select
                                                    name='robotsFollow'
                                                    value={values.robotsFollow}
                                                    onChange={(e) => setFieldValue('robotsFollow', e.target.value)}
                                                    className={inputClasses}
                                                    disabled={isDisabled}
                                                >
                                                    <option value='follow'>Yes (Follow)</option>
                                                    <option value='nofollow'>No (NoFollow)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <Input
                                            type='text'
                                            name='canonicalUrl'
                                            label='Canonical URL'
                                            placeholder='The canonical URL that this page should point to'
                                            disabled={isDisabled}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Analysis Results */}
                            <div className='border-t border-gray-200 dark:border-gray-700 pt-6'>
                                <h4 className='text-sm font-medium text-gray-800 dark:text-white mb-4 flex items-center gap-2'>
                                     <span className='w-1.5 h-5 bg-secondary-500 rounded-full' />
                                     SEO Analysis results
                                </h4>
                                <div className='space-y-3 pl-2'>
                                     {seoAnalysis.issues.length === 0 && seoAnalysis.good.length === 0 && (
                                         <div className='flex items-center gap-3 text-gray-500 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-700'>
                                             <HiOutlineInformationCircle className='w-5 h-5' />
                                             <span className='text-sm'>Enter a focus keyphrase above to generate an analysis.</span>
                                         </div>
                                     )}
                                     {seoAnalysis.issues.map((item) => (
                                         <AnalysisItem key={item.msg} level={item.level} msg={item.msg} />
                                     ))}
                                     {seoAnalysis.good.map((item) => (
                                         <AnalysisItem key={item.msg} level='green' msg={item.msg} />
                                     ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Readability Tab Content */}
                    {activeTab === 'readability' && (
                        <div className='space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300'>
                             <div className='border-l-4 border-info pl-4 py-3 bg-info-bg dark:bg-info-bg/10 rounded-r-lg'>
                                <h4 className='text-sm font-medium text-gray-800 dark:text-gray-100'>Readability Analysis</h4>
                                <p className='text-sm text-gray-600 dark:text-gray-300 mt-1'>We analyze the content complexity, sentence length, and paragraph structure.</p>
                            </div>
                            <div className='space-y-3 pl-2'>
                                 {readabilityAnalysis.issues.map((item) => (
                                     <AnalysisItem key={item.msg} level={item.level} msg={item.msg} />
                                 ))}
                                 {readabilityAnalysis.good.map((item) => (
                                     <AnalysisItem key={item.msg} level='green' msg={item.msg} />
                                 ))}
                                 {readabilityAnalysis.issues.length === 0 && readabilityAnalysis.good.length === 0 && (
                                     <div className='flex items-center gap-3 text-gray-500 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-700'>
                                        <HiOutlineInformationCircle className='w-5 h-5' />
                                        <span className='text-sm'>Please add content in the description to see readability score.</span>
                                     </div>
                                 )}
                            </div>
                        </div>
                    )}

                    {/* Schema Tab Content */}
                    {activeTab === 'schema' && (
                        <div className='space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300'>
                            <div className='space-y-6'>
                                <div className='p-4 bg-gray-50 dark:bg-gray-800/80 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 flex gap-3'>
                                    <HiOutlineInformationCircle className='w-12 h-12 text-primary-500 shrink-0' />
                                    <p>
                                        Schema markup is code that you put on your website to help the search engines return more informative results for users. 
                                        Configuring this correctly helps Google understand that this page is an <strong>{values.schemaPageType}</strong>.
                                    </p>
                                </div>

                                <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
                                    <div>
                                        <label className={labelClasses}>Page Type</label>
                                        <select
                                            name='schemaPageType'
                                            value={values.schemaPageType}
                                            onChange={(e) => setFieldValue('schemaPageType', e.target.value)}
                                            className={inputClasses}
                                            disabled={isDisabled}
                                        >
                                            {SCHEMA_PAGE_TYPES.map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </select>
                                        <p className='text-xs text-gray-500 mt-2'>What type of page is this? (e.g., Contact, About, etc.)</p>
                                    </div>
                                    <div>
                                        <label className={labelClasses}>Article Type</label>
                                        <select
                                            name='schemaArticleType'
                                            value={values.schemaArticleType}
                                            onChange={(e) => setFieldValue('schemaArticleType', e.target.value)}
                                            className={inputClasses}
                                            disabled={isDisabled}
                                        >
                                            {SCHEMA_ARTICLE_TYPES.map(type => (
                                                <option key={type.value} value={type.value}>{type.label}</option>
                                            ))}
                                        </select>
                                        <p className='text-xs text-gray-500 mt-2'>If this is a blog or news, specify the type.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                     <div className='flex justify-end pt-8 gap-3 border-t border-gray-100 dark:border-gray-700/50 mt-4'>
                        <button
                        type='submit'
                        className='px-8 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-lg shadow-primary-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-0.5 active:translate-y-0'
                        disabled={loading || !dirty || !isValid}
                        >
                        {loading ? 'Saving Changes...' : 'Save SEO Settings'}
                        </button>
                    </div>
                </div>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default SEO;
